/**
 * `@vaarta/auth-flow` — Identity Studio's configured sign-in plan, turned into something a
 * form can render. AG-1587, extracted from `api-workflow df639a6` (AG-1802).
 *
 * ⭐ THE DEFECT THIS CLOSES. Identity Studio's console has a "Sign-in flow" screen and a
 * "Login & MFA policy" screen, and an operator can reorder the steps, turn passkeys on,
 * allow one-time codes by email or WhatsApp, add magic links, or turn password sign-in
 * off. Measured 2026-09-11: **none of the four suite applications read any of it.** All
 * four hardcoded email + password in JSX, so every one of those switches was inert. The
 * console was honest about what it stored; nothing downstream asked.
 *
 * `GET {urls.flow}?journey=login&app_key={key}` answers the ordered plan, and Identity
 * Studio has ALREADY reconciled it against the policy before we see it — `password` is
 * absent when password sign-in is off, `passkey` when passwordless is off, `magic_link`
 * without a magic-link factor. ⇒ **Absent means absent.** Rendering a field the plan does
 * not carry is how you get a control that cannot succeed.
 *
 * ⚠️ WHY THIS IS A PACKAGE AND NOT A FOURTH COPY. On 2026-09-11 `decideCallbackArrival`
 * was found answering `hosted` in two applications and `form` in the other two — four
 * copies of one sign-in decision that the contract requires to be identical, drifted into
 * two answers, and it shipped an Identity Studio page to the users of two products
 * (AG-1583). A 13-step-type renderer across four journeys is a much larger version of that
 * bill. This module is therefore the single source of truth; see `README.md` for how the
 * copies are kept honest.
 *
 * ⚠️ Everything here except `fetchFlowPlan` is PURE — no `fetch`, no `window`, no DOM — so
 * it can be tested on the node environment where there is no `window`. `fetchFlowPlan` is
 * the one function that touches the network, it takes an injectable `fetchImpl`, and
 * importing this module performs no I/O.
 *
 * ⛔ Fail soft, everywhere. A plan we cannot read returns `null`, and the caller renders
 * `FALLBACK_LOGIN_SURFACE` — the fixed email+password form every application rendered
 * before this existed. Identity Studio 502s its siblings for about a minute on every
 * deploy, and a sign-in page is the worst thing in the estate to have fail then — the same
 * rule AG-1542 settled for session liveness: "could not ask" is not a verdict.
 */
/** The step types this renderer can actually collect today. */
const IDENTIFIER_TYPES = new Set(['email', 'username', 'phone']);
/**
 * Credential steps Identity Studio can put in a plan that we cannot yet render.
 *
 * ⛔ Listed rather than ignored. Each needs a ceremony no suite application has built —
 * `navigator.credentials` for passkey, the configured provider list for social — which is
 * the rest of AG-1805. Silently dropping them would make a deployment that enables one look
 * simply broken.
 *
 * ⚠️ `magic_link` LEFT this set in AG-1805: it is rendered when the consuming application
 * declares it has somewhere for the emailed link to land (`can.magicLink`), and named here
 * when it does not. Passkey stays until the WebAuthn relying-party origin is decided — a
 * passkey enrolled on `dev.identity.vaarta.ai` will not assert on an application host, and
 * getting it wrong strands every credential already enrolled.
 */
const UNSUPPORTED_TYPES = new Set(['passkey', 'social']);
/**
 * Second-factor methods an application's own sign-in can actually complete today.
 *
 * ⚠️⚠️ THE REASON FOR THIS SET CHANGED TWICE AND THE SET CHANGED ONCE. Both corrections are
 * kept in full, because each was true when it was written and acting on a stale one is the
 * mistake this comment exists to prevent.
 *
 * (1) WHAT THIS SAID FIRST, true on 2026-09-14: `sdk_login` hardcoded `"methods": ["totp"]`
 * whatever the policy allowed, `mfa/send` refused any non-`totp` method at sign-in with 400,
 * and `mfa/verify` checked with `_sdk_totp_check`. So the delivered channels existed only as
 * a step-up for a session already signed in, and a login plan advertising `otp_email` was
 * advertising something the SERVER could not do.
 *
 * (2) ⭐ AG-1943 CLOSED THAT, hours later, and the set stayed narrow for a NEW reason: no
 * suite application rendered a channel picker. A method this set admits is a method some page
 * must be able to choose, send and collect, and widening it before a page can do that draws a
 * control that goes nowhere — the same defect one level down, with the blame moved.
 *
 * (3) ⭐ AG-1805 CLOSED *THAT* — and only now does the set move. All four suite applications
 * render the picker `readMfaChallenge()` below feeds, proxy `mfa/send`, and complete the
 * sign-in through `mfa/verify` against the challenge the password step minted. The order was
 * the point: the pages first, the vocabulary second.
 *
 * ⛔ WHAT IS STILL OUT, and each for its own reason rather than for want of a line here:
 *
 *   push_approval       AG-1984's prompt on a bound device. There is no code to collect —
 *                       completing it is a POLL, not an input — so a code box would be the
 *                       wrong control rather than a missing one.
 *   magic_link_*        A LINK, in the vocabulary; measured in `sdk_mfa_send`, a *_sms/email
 *                       magic-link method at sign-in dispatches `auth.otp` and delivers a
 *                       NUMERIC CODE. Admitting it would draw "email me a link" over a box
 *                       that wants six digits — a control that lies about what it does.
 * ⭐ `recovery_code` JOINED ON 2026-09-16, in the edit that measured its route live — which
 * is the promise the held-back note above it used to make. Measured three ways, because
 * "it is merged" is a claim about a branch and not about a running server:
 *
 *   1. `d6d34d58` and `253aa279` are ancestors of `origin/release/dev` AND of
 *      `origin/release/production`;
 *   2. the DEPLOYED container — `docker exec identity-studio-be grep …` on the dev box —
 *      carries `availability["methods"] = [*availability["methods"], "recovery_code"]` at
 *      line 2023, which is the line that puts the method in a sign-in's offer;
 *   3. and the offer and the check are the SAME commit, so a server that offers it can
 *      always verify it. That is what makes this safe rather than merely likely: a build
 *      without the code never names the method, so this set admits something it never sees.
 *
 * ⚠️ IT IS NOT A POLICY METHOD, and a page must not treat it as one. `sdk_login` appends it
 * OUTSIDE `available_second_factors`, only when the person has codes left and only beside a
 * real factor — "recovery codes are allowed by nobody; they are minted with the
 * authenticator and belong to the person, not to the configuration." ⇒ It cannot be switched
 * on, it will not appear for somebody who never enrolled, and it is appended LAST because it
 * is the fallback you reach for when the methods above it are out of reach. Preserving the
 * server's order — which this module already does — is what keeps it last on the page.
 *
 * ⭐ Why every correction is kept: a comment describing the server is a CLAIM about the
 * server, and this file's own history is the argument for re-measuring one before acting on
 * it. Each entry above is a measurement, not a recollection.
 */
const DELIVERABLE_MFA_METHODS = new Set([
    'totp',
    'otp_email',
    'otp_sms',
    'otp_whatsapp',
    'recovery_code',
]);
/**
 * Does this second-factor method have to be SENT before it can be entered?
 *
 * TOTP does not — the code is already on the person's device — and every delivered channel
 * does. AG-1805: "a UI that always calls `send` or never calls it is wrong for half the
 * configurations." Exported because the answer belongs with the method vocabulary rather
 * than in each page's JSX, and because it stays correct on the day the delivered channels
 * become reachable at sign-in.
 */
export function mfaMethodNeedsSend(method) {
    const asked = text(method).toLowerCase();
    // ⛔ NEITHER OF THESE IS DELIVERED, and the second is easy to miss. TOTP is already on the
    // person's device; a RECOVERY CODE is one of ten strings they were shown once at enrolment
    // and told to write down — there is nothing to send, and nowhere to send it. Before
    // AG-1584 this returned `true` for anything that was not `totp`, so the day Identity Studio
    // began offering `recovery_code` a page would have called `mfa/send` for it.
    // ⛔ AG-1835 — nor is a PUSH APPROVAL sent through `mfa/send`: it is raised on the device by `auth/push/start` and
    // polled. Before a renderer could declare push it never reached this, so the answer did not matter; now it does.
    return asked !== 'totp' && asked !== 'recovery_code' && asked !== PUSH_APPROVAL;
}
/**
 * What to CALL a second-factor method, in a sentence a person reads.
 *
 * ⚠️ Here rather than in four pages' JSX for the reason `decideCallbackArrival` is here: the
 * same four copies of the same decision drifted into two answers once already (AG-1583), and
 * a label is a decision — "Email me a code" and "Send a code to my email" in two products is
 * the suite reading as four products, which is the whole of AG-1532.
 *
 * ⛔ An UNKNOWN method gets the wire name back, not "Unknown" and not an empty string. The
 * method is real — Identity Studio offered it — and a page that draws a blank button has lost
 * information a person needs to choose. Reading `otp_telegram` on a control is ugly; reading
 * nothing is a dead end.
 */
const MFA_METHOD_LABELS = {
    totp: 'Authenticator app',
    otp_email: 'Email me a code',
    otp_sms: 'Text me a code',
    otp_whatsapp: 'Message me on WhatsApp',
    recovery_code: 'Use a recovery code',
    push_approval: 'Approve on a registered device',
    magic_link_email: 'Email me a sign-in link',
};
export function mfaMethodLabel(method) {
    const asked = text(method).toLowerCase();
    return MFA_METHOD_LABELS[asked] || asked;
}
export function mfaCodeInput(method) {
    if (text(method).toLowerCase() === 'recovery_code') {
        return {
            inputMode: 'text',
            // ⛔ NOT `one-time-code`. These are saved, not delivered — offering the browser's SMS
            // autofill for one invites it to paste a code from a message that is not this.
            autoComplete: 'off',
            placeholder: 'xxxxx-xxxxx',
            maxLength: 32,
        };
    }
    return {
        inputMode: 'numeric',
        autoComplete: 'one-time-code',
        placeholder: '123456',
        maxLength: 12,
    };
}
/**
 * Read an `mfa_required` answer into something a picker can render — AG-1805.
 *
 * Returns `null` for anything that is not one, so a caller can ask this of every login
 * response without first branching on `next_step`.
 *
 * ⚠️ AN EMPTY OR ABSENT `methods` MEANS TOTP HERE, and that is the opposite of the ruling
 * `splitMfaMethods` makes three hundred lines down — deliberately, because the two answer
 * different questions. There, absence is a plan that did not say, and inventing a method
 * would put `mfa:` noise in the console of every application reading an older plan. HERE the
 * server has already decided somebody needs a second factor NOW, and rendering no control at
 * all would strand them mid-sign-in. Every one of the four backends already substituted
 * `["totp"]` at exactly this point before this function existed; this keeps that behaviour in
 * one place instead of four.
 */
export function readMfaChallenge(payload, can = {}) {
    if (!payload || typeof payload !== 'object')
        return null;
    const body = payload;
    if (text(body.next_step) !== 'mfa_required')
        return null;
    const offered = Array.isArray(body.methods)
        ? body.methods.map((m) => text(m).toLowerCase()).filter(Boolean)
        : [];
    const hints = body.method_hints && typeof body.method_hints === 'object'
        ? body.method_hints
        : {};
    const choices = [];
    const unsupportedMethods = [];
    const seen = new Set();
    for (const method of offered.length ? offered : ['totp']) {
        if (seen.has(method))
            continue;
        seen.add(method);
        // AG-1835 — push approval is completed by POLLING a request the person answers on a registered device, not by
        // typing a code, so only a renderer that declares it can poll (`can.pushApproval`) is offered it. The rest see it
        // named in `unsupportedMethods`, exactly as before.
        const renderable = DELIVERABLE_MFA_METHODS.has(method) || (method === PUSH_APPROVAL && can.pushApproval === true);
        if (!renderable) {
            unsupportedMethods.push(method);
            continue;
        }
        choices.push({
            method,
            label: mfaMethodLabel(method),
            hint: text(hints[method]) || null,
            needsSend: mfaMethodNeedsSend(method),
            input: mfaCodeInput(method),
        });
    }
    const expires = Number(body.expires_in_seconds);
    return {
        challengeId: text(body.challenge_id) || null,
        choices,
        unsupportedMethods,
        message: text(body.message) || 'Enter the code from your authenticator app.',
        expiresInSeconds: Number.isFinite(expires) && expires > 0 ? expires : null,
    };
}
/** The wire name of the push-approval second factor (AG-1984), which a renderer completes by polling. */
export const PUSH_APPROVAL = 'push_approval';
/**
 * The body of `POST …/mfa/verify` for one of the challenge's own choices — AG-1835. `null` when there is nothing
 * honest to send: no challenge (an older backend — take the one-shot path instead), a method the challenge did not
 * offer, or an empty code.
 *
 * ⛔ THE METHOD IS NAMED BY THE CALLER'S CHOICE, NEVER INFERRED FROM WHAT WAS TYPED. The operator console once guessed
 * a recovery code from a hyphen (AG-805); `mfa/verify` takes `method` as its own field for that reason, and every page
 * that completes a sign-in builds its body here so none of them can re-invent the guess. For `push_approval` the
 * `code` is the approval ticket the poll returned.
 */
export function mfaVerifyBody(challenge, method, code) {
    if (!challenge || !challenge.challengeId)
        return null;
    const asked = text(method).toLowerCase();
    if (!challenge.choices.some((c) => c.method === asked))
        return null;
    const value = text(code);
    if (!value)
        return null;
    return { challenge_id: challenge.challengeId, method: asked, code: value };
}
function text(value) {
    return typeof value === 'string' ? value.trim() : '';
}
/**
 * Parse `/auth/flow`'s answer, or `null` if it is not a usable plan.
 *
 * Tolerant on purpose about everything except the one thing that matters — an ordered list
 * of typed steps. Identity Studio may add fields to this payload at any time and an older
 * frontend must keep working.
 */
export function readLoginPlan(payload) {
    if (!payload || typeof payload !== 'object')
        return null;
    const body = payload;
    if (!Array.isArray(body.steps))
        return null;
    const steps = [];
    for (const raw of body.steps) {
        if (!raw || typeof raw !== 'object')
            continue;
        const row = raw;
        const type = text(row.type);
        if (!type)
            continue; // a step with no type is not renderable, and guessing is worse
        steps.push({
            type,
            label: text(row.label) || type,
            field: text(row.field) || null,
            required: row.required === true,
            order: typeof row.order === 'number' ? row.order : steps.length + 1,
            conditional: text(row.conditional) || null,
            methods: Array.isArray(row.methods) ? row.methods.map(text).filter(Boolean) : [],
        });
    }
    if (steps.length === 0)
        return null;
    // Sort by the plan's own `order`, not by array position: the console writes `order` and
    // the two have already been observed to agree only by coincidence.
    steps.sort((a, b) => a.order - b.order);
    return { steps };
}
function identifierField(step) {
    if (step.type === 'phone') {
        return {
            kind: 'identifier',
            stepType: step.type,
            label: step.label,
            inputType: 'tel',
            autoComplete: 'tel',
        };
    }
    if (step.type === 'username') {
        return {
            kind: 'identifier',
            stepType: step.type,
            label: step.label,
            inputType: 'text',
            autoComplete: 'username',
        };
    }
    return {
        kind: 'identifier',
        stepType: step.type,
        label: step.label,
        inputType: 'email',
        autoComplete: 'email',
    };
}
/**
 * What to render, from the plan. `null` when there is no usable plan — render the fallback.
 */
export function planToSurface(plan, can = {}) {
    if (!plan)
        return null;
    const canCollectMfa = can.mfa !== false;
    const canLandMagicLink = can.magicLink === true;
    const fields = [];
    const unsupported = [];
    let hasIdentifier = false;
    let hasPassword = false;
    let hasMfaStep = false;
    let hasMagicLink = false;
    for (const step of plan.steps) {
        if (IDENTIFIER_TYPES.has(step.type)) {
            // One identifier. A plan offering email AND username means "either", and rendering
            // both would ask a person to fill in two boxes to name themselves once.
            if (!hasIdentifier) {
                fields.push(identifierField(step));
                hasIdentifier = true;
            }
            continue;
        }
        if (step.type === 'password') {
            fields.push({ kind: 'password', label: step.label });
            hasPassword = true;
            continue;
        }
        if (step.type === 'mfa') {
            // ⚠️ An application that cannot collect a second factor says so, and the step is
            // NAMED rather than dropped. It does NOT make the plan unusable: the step is
            // conditional (`if_mfa_enabled`), so most people on such a deployment sign in
            // normally, and refusing the whole form would lock out everybody to describe a
            // problem that affects some.
            if (!canCollectMfa) {
                unsupported.push(step.type);
                continue;
            }
            // Rendered only once a sign-in attempt actually asks for it — the plan says the step
            // EXISTS, the attempt says whether this person needs it. Carrying it here is what
            // lets the label and the accepted methods come from configuration.
            const { methods, unsupportedMethods } = splitMfaMethods(step.methods);
            fields.push({ kind: 'mfa', label: step.label, methods, unsupportedMethods });
            for (const method of unsupportedMethods)
                unsupported.push(`mfa:${method}`);
            hasMfaStep = true;
            continue;
        }
        if (step.type === 'magic_link') {
            // ⛔ Rendered ONLY where the emailed link has somewhere to land. See
            // `RendererCapabilities.magicLink` for why this one capability defaults to false.
            if (!canLandMagicLink) {
                unsupported.push(step.type);
                continue;
            }
            fields.push({ kind: 'magic_link', label: step.label });
            hasMagicLink = true;
            continue;
        }
        if (UNSUPPORTED_TYPES.has(step.type)) {
            unsupported.push(step.type);
            continue;
        }
        // A type nobody here has seen. Name it rather than dropping it: a required step this
        // renderer cannot collect is a sign-in that will fail for a reason the user cannot act
        // on, and a silent drop makes that invisible in every environment at once.
        unsupported.push(step.type);
    }
    return {
        fields,
        hasPassword,
        hasMfaStep,
        hasMagicLink,
        unsupported,
        // ⭐ A magic link is a CREDENTIAL, not a convenience: `auth_flow.py` lists it in
        // `CREDENTIAL_STEPS` beside `password`, and `magic-link/verify` mints a full session.
        // So a plan with password sign-in turned OFF and magic link on is usable here — which
        // is the first configuration this renderer has ever been able to satisfy without a
        // password, and exactly what AG-1805's "disabling Password sign-in leaves a usable
        // sign-in rather than a dead form" asks for.
        usable: hasIdentifier && (hasPassword || hasMagicLink),
    };
}
/**
 * Split a plan's `mfa` methods into the ones a sign-in here can complete and the ones it
 * cannot. See `DELIVERABLE_MFA_METHODS` for the measurement behind the split.
 *
 * ⚠️ An EMPTY `methods` array is not "no methods" — it is a plan that did not say, which is
 * what every pre-AG-1587 payload and the fallback surface carry. It stays empty and is not
 * reported as unsupported: reporting absence as a refusal would put `mfa:` noise in the
 * console of every application that reads an older plan.
 */
function splitMfaMethods(methods) {
    const deliverable = [];
    const rest = [];
    for (const method of methods) {
        (DELIVERABLE_MFA_METHODS.has(method.toLowerCase()) ? deliverable : rest).push(method);
    }
    return { methods: deliverable, unsupportedMethods: rest };
}
/** Convenience for the caller: parse and shape in one step. */
export function loginSurfaceFrom(payload, can = {}) {
    return planToSurface(readLoginPlan(payload), can);
}
/**
 * What every suite application's sign-in page rendered before AG-1802, as a `LoginSurface`.
 *
 * ⛔ A constant rather than a second JSX branch, so a consuming page has exactly ONE render
 * path. Two branches drift, and this epic has already paid that bill once (AG-1583).
 *
 * ⚠️ Frozen, because it is module-level shared state: four pages read the same object, and
 * one of them mutating it would change the fallback for the others in the same bundle.
 */
function buildFallbackSurface() {
    const surface = {
        fields: [
            {
                kind: 'identifier',
                stepType: 'email',
                label: 'Email',
                inputType: 'email',
                autoComplete: 'email',
            },
            { kind: 'password', label: 'Password' },
            { kind: 'mfa', label: 'Authentication code', methods: [], unsupportedMethods: [] },
        ],
        hasPassword: true,
        hasMfaStep: true,
        // ⛔ The fallback never offers a magic link. It is what a page renders when the plan
        // could not be read, and "could not ask" is not a verdict — least of all a verdict that
        // this deployment has magic link turned on.
        hasMagicLink: false,
        unsupported: [],
        usable: true,
    };
    // Frozen as statements rather than with `Object.freeze(...) as LoginSurface`: the literal
    // form infers `readonly` members that then have to be cast back through `unknown`, and a
    // double cast here would silently stop checking the one object four sign-in pages share.
    for (const field of surface.fields)
        Object.freeze(field);
    Object.freeze(surface.fields);
    Object.freeze(surface.unsupported);
    return Object.freeze(surface);
}
export const FALLBACK_LOGIN_SURFACE = buildFallbackSurface();
/**
 * The URL to ask for a journey's plan, or `null` when this deployment cannot be asked.
 *
 * `null` rather than a guess: `urls.flow` is absent on a backend older than AG-1802, and a
 * constructed fallback URL would be a plausible wrong answer — the failure mode the auth
 * contract's `urls.*` rule exists to prevent.
 */
export function buildFlowUrl(config, journey = 'login') {
    const base = text(config?.urls?.flow);
    const appKey = text(config?.app_key);
    if (!base || !appKey)
        return null;
    return `${base}?journey=${encodeURIComponent(journey)}&app_key=${encodeURIComponent(appKey)}`;
}
/**
 * Ask Identity Studio for a journey's configured plan. `null` on every failure.
 *
 * ⚠️ The ONE impure function here. Importing this module does no I/O; calling this does.
 *
 * ⛔ `credentials: 'omit'`. This route is unauthenticated by design and takes no cookie —
 * it is the only Identity Studio URL in `/v1/auth/config`'s `urls` block that lives on the
 * API host rather than the hosted host, precisely because it is XHR data rather than a page
 * the browser is sent to. Measured 2026-09-11 from real Chromium pages on all four suite
 * origins: the derived CORS allowlist admits each application's own origin, so the browser
 * may read the answer directly and no backend proxy is needed. ⚠️ Note that `curl` with a
 * hand-set `Origin` proves nothing about CORS; that measurement was taken in a browser.
 *
 * Returns the raw payload rather than a `LoginSurface` so a caller can log or inspect what
 * Identity Studio actually said before it is shaped.
 */
export async function fetchFlowPlan(config, options = {}) {
    const url = buildFlowUrl(config, options.journey ?? 'login');
    if (!url)
        return null;
    const doFetch = options.fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
    if (!doFetch)
        return null;
    try {
        const response = await doFetch(url, { credentials: 'omit' });
        if (!response.ok)
            return null;
        return await response.json();
    }
    catch {
        return null;
    }
}
/** Ask for the login plan and shape it, falling back to `null` for the caller to handle. */
export async function fetchLoginSurface(config, options = {}) {
    return loginSurfaceFrom(await fetchFlowPlan(config, { ...options, journey: 'login' }), options.can ?? {});
}
/**
 * Read `challenge_id` and `token` off the URL the emailed link opened, or `null`.
 *
 * ⭐ Here rather than in four pages because it is the same four-line parse with the same
 * four-way branch on "one of them is missing", and that is precisely the shape AG-1583 paid
 * for: `decideCallbackArrival` was four copies of one decision and drifted into two answers.
 *
 * ⛔ BOTH or neither. A `challenge_id` with no `token` is not a partial sign-in to be
 * retried with a prompt — it is a link that was truncated, rewritten by a mail scanner, or
 * hand-edited, and the honest answer is the one the page already has to render: "this link
 * is not usable, ask for another." Returning a half-callback would invite a caller to POST
 * it and turn a broken link into a 401 the person cannot act on.
 *
 * ⚠️ Takes anything `URLSearchParams`-shaped, or a query/URL string, so a Next.js
 * `useSearchParams()`, a React Router `URLSearchParams`, and `window.location.search` all
 * satisfy it without the page converting. The package stays DOM-free: nothing here reads
 * `window`.
 */
export function readMagicLinkCallback(source) {
    if (!source)
        return null;
    let params;
    if (typeof source === 'string') {
        try {
            // `URL` first so a whole href works; a bare `?a=b` or `a=b` falls through to the
            // query parser. Neither throws on input this is given by mistake.
            params = source.includes('://')
                ? new URL(source).searchParams
                : new URLSearchParams(source.startsWith('?') ? source.slice(1) : source);
        }
        catch {
            return null;
        }
    }
    else if (typeof source.get === 'function') {
        params = source;
    }
    else {
        return null;
    }
    const challengeId = text(params.get('challenge_id'));
    const token = text(params.get('token'));
    if (!challengeId || !token)
        return null;
    return { challengeId, token };
}
/**
 * What to render for `journey=forgot_password`.
 *
 * ⛔ A `password` step in a RECOVERY plan is dropped rather than rendered. Recovery is the
 * journey for somebody who does not have the password; a field asking for it is the one
 * control guaranteed to be unfillable by the person on that page.
 */
export function recoveryPlanToSurface(plan, can = {}) {
    if (!plan)
        return null;
    const canCollectMfa = can.mfa !== false;
    const fields = [];
    const unsupported = [];
    let hasIdentifier = false;
    let hasMfaStep = false;
    for (const step of plan.steps) {
        if (IDENTIFIER_TYPES.has(step.type)) {
            if (!hasIdentifier) {
                fields.push(identifierField(step));
                hasIdentifier = true;
            }
            continue;
        }
        if (step.type === 'password')
            continue; // see the docstring above
        if (step.type === 'mfa') {
            // ⚠️ Valid in recovery: a deployment may require a second factor before it will send
            // a reset link at all. Today no application renders one, which is why an unrenderable
            // mfa step has to be NAMED rather than dropped.
            if (!canCollectMfa) {
                unsupported.push(step.type);
                continue;
            }
            const { methods, unsupportedMethods } = splitMfaMethods(step.methods);
            fields.push({ kind: 'mfa', label: step.label, methods, unsupportedMethods });
            for (const method of unsupportedMethods)
                unsupported.push(`mfa:${method}`);
            hasMfaStep = true;
            continue;
        }
        // ⛔ `magic_link` is a step type RECOVERY can carry (`auth_flow.py` lists it for LOGIN
        // and RECOVERY alike) and it is NOT rendered here, whatever the application declares.
        // A magic link signs somebody in; it does not reset a password. Offering it on the
        // recovery page would answer a person who has lost their password with a way to skip
        // having one — silently changing what the page does, on a page reached by people who
        // are already confused about their credentials.
        if (UNSUPPORTED_TYPES.has(step.type) || step.type === 'magic_link') {
            unsupported.push(step.type);
            continue;
        }
        unsupported.push(step.type);
    }
    return { fields, hasMfaStep, unsupported, usable: hasIdentifier };
}
/** Parse and shape a recovery plan in one step. */
export function recoverySurfaceFrom(payload, can = {}) {
    return recoveryPlanToSurface(readLoginPlan(payload), can);
}
/**
 * What every application's forgot-password page asks for when the plan cannot be read.
 * Frozen, for the same reason as `FALLBACK_LOGIN_SURFACE`.
 */
export const FALLBACK_RECOVERY_SURFACE = (() => {
    const surface = {
        fields: [
            {
                kind: 'identifier',
                stepType: 'email',
                label: 'Email',
                inputType: 'email',
                autoComplete: 'email',
            },
        ],
        hasMfaStep: false,
        unsupported: [],
        usable: true,
    };
    for (const field of surface.fields)
        Object.freeze(field);
    Object.freeze(surface.fields);
    Object.freeze(surface.unsupported);
    return Object.freeze(surface);
})();
/** Ask for the recovery plan and shape it. */
export async function fetchRecoverySurface(config, options = {}) {
    return recoverySurfaceFrom(await fetchFlowPlan(config, { ...options, journey: 'forgot_password' }), options.can ?? {});
}
/**
 * Step types that are a plain typed box, with the autofill hint each one deserves.
 *
 * ⚠️ `autoComplete` is not decoration. A sign-up form whose name box says `off` makes every
 * password manager offer to save nothing useful, and `new-password` (below) is what tells
 * one to GENERATE rather than to fill the password it already has for this site.
 */
const REGISTRATION_TEXT_TYPES = {
    name: { autoComplete: 'name' },
    company: { autoComplete: 'organization' },
    org_name: { autoComplete: 'organization' },
    workspace_name: { autoComplete: 'off' },
    custom_field: { autoComplete: 'off' },
};
/**
 * Steps that are part of the FLOW rather than of the form.
 *
 * ⛔ Neither rendered nor reported unsupported, and the distinction matters. `verify_email`
 * says the platform will send a verification mail after the POST; `redirect` says where to
 * go afterwards. Neither is something a person types. Calling them "unsupported" would put
 * permanent noise in the console of every correctly configured deployment — and
 * "unsupported" is a word this package spends on things an operator has to act on.
 */
const REGISTRATION_FLOW_TYPES = new Set(['redirect', 'verify_email']);
/**
 * ⛔ Where the `country` step's options come from, and why no browser may just ask for them.
 *
 * `GET /iam/sdk/master/countries` is authenticated by the APPLICATION's SDK secret, so it
 * answers 401 to a browser — correctly, and measured. An application proxies it, exactly as
 * §5 of the auth contract has each one proxy the password policy. Named here so four pages
 * do not each invent a different path for the same list.
 */
export const COUNTRY_SOURCE = 'master/countries';
/**
 * What a registration page should render, from the plan.
 *
 * ⭐ Absent means absent, as everywhere else here: `auth_flow.py` has already reconciled the
 * plan against the policy, so `password` is gone when password sign-in is off. A page renders
 * exactly what the plan carries.
 */
export function registrationPlanToSurface(plan, journey, can = {}) {
    if (!plan)
        return null;
    const canPickCountry = can.countries === true;
    const fields = [];
    const unsupported = [];
    const requiredUnsupported = [];
    let hasIdentifier = false;
    let hasPassword = false;
    let expectsEmailVerification = false;
    const refuse = (step) => {
        unsupported.push(step.type);
        if (step.required)
            requiredUnsupported.push(step.type);
    };
    for (const step of plan.steps) {
        if (REGISTRATION_FLOW_TYPES.has(step.type)) {
            if (step.type === 'verify_email')
                expectsEmailVerification = true;
            continue;
        }
        if (IDENTIFIER_TYPES.has(step.type)) {
            // One identifier, for the reason the login renderer gives: a plan offering email AND
            // username means "either", and two boxes to name yourself once is not a form.
            if (!hasIdentifier) {
                const base = identifierField(step);
                fields.push({
                    kind: 'identifier',
                    stepType: base.stepType,
                    field: step.field || step.type,
                    label: base.label,
                    required: step.required,
                    inputType: base.inputType,
                    autoComplete: base.autoComplete,
                });
                hasIdentifier = true;
            }
            continue;
        }
        if (step.type === 'password') {
            // ⛔ NEVER IN ONBOARDING, and this is the same ruling `recoveryPlanToSurface` makes
            // about the same step type. Onboarding runs for somebody who is ALREADY SIGNED IN —
            // they are naming their organization, not proving who they are. A password box there
            // is a control that cannot succeed (nothing would check it) and, worse, it is
            // phishing-shaped: a form that asks for a password after sign-in, on a page reached
            // by a redirect, is exactly what people are taught to distrust.
            //
            // ⚠️ Dropped rather than named `unsupported`, because it is not a gap for an operator
            // to act on — the step simply does not belong to this journey.
            if (journey === 'onboarding')
                continue;
            fields.push({
                kind: 'password',
                stepType: step.type,
                field: step.field || 'password',
                label: step.label,
                required: step.required,
            });
            hasPassword = true;
            continue;
        }
        if (step.type === 'consent') {
            fields.push({
                kind: 'consent',
                stepType: step.type,
                field: step.field || 'accept_terms',
                label: step.label,
                required: step.required,
            });
            continue;
        }
        if (step.type === 'country') {
            // ⛔ Only where the application has somewhere to get the list. The same rule, and the
            // same reason, as `magicLink`: a select with no options is a control that cannot
            // succeed, and drawing one is worse than naming the gap to the operator who enabled
            // the step. ⚠️ `country` is optional in every plan measured, so an application without
            // the proxy still has a completable onboarding form.
            if (!canPickCountry) {
                refuse(step);
                continue;
            }
            fields.push({
                kind: 'country',
                stepType: step.type,
                field: step.field || 'country',
                label: step.label,
                required: step.required,
                source: COUNTRY_SOURCE,
            });
            continue;
        }
        const textType = REGISTRATION_TEXT_TYPES[step.type];
        if (textType && step.field) {
            fields.push({
                kind: 'text',
                stepType: step.type,
                field: step.field,
                label: step.label,
                required: step.required,
                autoComplete: textType.autoComplete,
            });
            continue;
        }
        // `plan_select` needs the plan catalogue and a price; `timezone` needs a picker; a
        // credential step belongs to sign-in and not here; and a type nobody has seen is exactly
        // what naming exists for. All of them: named, never dropped.
        refuse(step);
    }
    return {
        journey,
        fields,
        hasPassword,
        expectsEmailVerification,
        // ⚠️ Always null from the PLAN: it is a sibling key of `steps` in the flow response, not
        // a step, so only `registrationSurfaceFrom` — which sees the raw payload — can fill it.
        // A caller shaping a plan it parsed itself gets null rather than a guess.
        identityStudioRedirect: null,
        unsupported,
        requiredUnsupported,
        usable: requiredUnsupported.length === 0 &&
            (journey === 'signup' ? hasIdentifier && hasPassword : fields.length > 0),
    };
}
/** Parse and shape a registration plan in one step. */
export function registrationSurfaceFrom(payload, journey, can = {}) {
    const surface = registrationPlanToSurface(readLoginPlan(payload), journey, can);
    if (!surface)
        return null;
    // ⚠️ Read off the RAW payload, not off `steps`: `redirect` is a sibling key of `steps` in
    // the flow response, and `auth_flow.py` also has a `redirect` STEP type. Reading only the
    // step would miss the key every measured plan actually carries.
    const body = payload;
    const target = typeof body?.redirect === 'string' ? body.redirect.trim() : '';
    return { ...surface, identityStudioRedirect: target || null };
}
/**
 * What a sign-up page asks for when the plan cannot be read.
 *
 * ⛔ The measured plan, identical across all four app keys on 2026-09-15 — a fallback that
 * matches what the deployment actually serves, so the fail-soft path renders the same form
 * rather than a second, different one. Frozen, for the reason `FALLBACK_LOGIN_SURFACE` is.
 *
 * ⚠️ `expectsEmailVerification` is FALSE here and that is deliberate. "Could not ask" is not
 * a verdict, least of all the verdict that this deployment mails a verification link — and
 * the sign-up RESPONSE settles it anyway.
 */
export const FALLBACK_SIGNUP_SURFACE = (() => {
    const surface = {
        journey: 'signup',
        fields: [
            {
                kind: 'text',
                stepType: 'name',
                field: 'name',
                label: 'Full name',
                required: true,
                autoComplete: 'name',
            },
            {
                kind: 'identifier',
                stepType: 'email',
                field: 'email',
                label: 'Email',
                required: true,
                inputType: 'email',
                autoComplete: 'email',
            },
            {
                kind: 'password',
                stepType: 'password',
                field: 'password',
                label: 'Password',
                required: true,
            },
        ],
        hasPassword: true,
        expectsEmailVerification: false,
        identityStudioRedirect: null,
        unsupported: [],
        requiredUnsupported: [],
        usable: true,
    };
    for (const field of surface.fields)
        Object.freeze(field);
    Object.freeze(surface.fields);
    Object.freeze(surface.unsupported);
    Object.freeze(surface.requiredUnsupported);
    return Object.freeze(surface);
})();
/** Ask for a registration plan and shape it. */
export async function fetchRegistrationSurface(config, journey, options = {}) {
    return registrationSurfaceFrom(await fetchFlowPlan(config, { ...options, journey }), journey, options.can ?? {});
}
/**
 * The application-owned auth surfaces a 401 must NEVER bounce to `/login`.
 *
 * ⛔⛔ THE DEFECT THIS PINS, measured across all four on 2026-09-15. Every application's
 * `api.ts` sends a 401 straight to `/login`, and a signed-out browser can still be holding a
 * stale token — so the bootstrap's `GET /v1/auth/me` answers 401 and REPLACES the page. That
 * throws somebody:
 *
 *   * off the recovery form mid-flow;
 *   * off the reset page while holding a single-use token they cannot get back;
 *   * off the verification page holding the same;
 *   * and off the SIGN-UP form half-way through typing an account that does not exist yet,
 *     to a sign-in they cannot complete.
 *
 * ⇒ In every case the person ejected is precisely the one least able to recover from it.
 *
 * ⚠️ Analytics fixed this for `/forgot-password` and `/reset-password` (AG-1804) and the
 * other three never did — and even Analytics left `/verify-email` and `/magic-link` out. That
 * is the "fixing an instance leaves the class" shape this programme keeps paying for, so the
 * SET lives here rather than in four `api.ts` files, and adding a surface fixes four
 * applications at once.
 *
 * ⚠️ An application ORs its own non-auth public surfaces (a public share page, an embed) on
 * top; this names only the ones the suite has in common.
 */
export const AUTH_SURFACE_PATHS = Object.freeze([
    '/signup',
    '/forgot-password',
    '/reset-password',
    '/verify-email',
    '/magic-link',
]);
/** Is this path one of the application-owned auth surfaces? Prefix match, so `/reset-password?token=…` counts. */
export function isAuthSurface(pathname) {
    const path = text(pathname);
    return AUTH_SURFACE_PATHS.some((surface) => path === surface || path.startsWith(`${surface}/`) || path.startsWith(`${surface}?`));
}
/**
 * Parse an application's `GET /v1/auth/countries` answer — AG-1803.
 *
 * ⛔ THE LIST IS PROXIED, NEVER FETCHED BY THE BROWSER. `GET /iam/sdk/master/countries` is
 * authenticated by the application's SDK secret and answers 401 to a page — measured. Each
 * application proxies it, exactly as §5 of the auth contract has each one proxy the password
 * policy, so this reads the APPLICATION's shape (`{"countries": [...] | null}`) and not
 * Identity Studio's (a bare array).
 *
 * ⚠️ `null` and `[]` MEAN DIFFERENT THINGS and this preserves the difference. `null` is
 * "could not ask" — the four proxies answer it rather than a 503 — and a page must then
 * render the field with no select rather than an empty one. `[]` would be a claim that the
 * deployment serves no countries.
 *
 * ⚠️ Rows without a `code` are dropped: a select option needs a value, and one without a
 * value submits nothing.
 */
export function readCountries(payload) {
    const body = payload;
    const rows = body?.countries;
    if (!Array.isArray(rows))
        return null;
    const out = [];
    for (const raw of rows) {
        if (!raw || typeof raw !== 'object')
            continue;
        const row = raw;
        const code = text(row.code);
        if (!code)
            continue;
        out.push({ code, name: text(row.name) || code });
    }
    return out;
}
/**
 * Turn what a person typed into the body to POST.
 *
 * ⭐ IN THE PACKAGE RATHER THAN IN FOUR PAGES, because every rule below is a fact about what
 * Identity Studio accepts — not about how a page looks — and four copies of a fact is the
 * shape AG-1583 already paid for once.
 *
 * ⛔⛔ CONSENT IS SENT AS A BOOLEAN. `auth_flow.missing_required_fields` special-cases the
 * `consent` step with `bool(value)` — it is the one step whose emptiness is specifically
 * FALSENESS — and in Python the string `"false"` is TRUE. A page that forwarded a checkbox's
 * string value would therefore satisfy a consent the person did not give, server-side, with
 * nothing looking wrong.
 *
 * ⚠️ AN EMPTY OPTIONAL FIELD IS OMITTED, not sent as `""`. An empty string is a VALUE: it
 * would store a blank where the person meant "leave it unset", and on a required field it
 * turns a refusal the server would word clearly into one about a field that is present but
 * empty.
 *
 * ⚠️ The identifier is lowercased, because that is what every one of the four backends does
 * to it before forwarding, and a page that sends mixed case makes the address a person sees
 * echoed back differ from the one they typed.
 */
export function buildRegistrationPayload(surface, values, consents = {}) {
    const payload = {};
    for (const field of surface.fields) {
        if (field.kind === 'consent') {
            payload[field.field] = Boolean(consents[field.field]);
            continue;
        }
        const raw = text(values[field.field]);
        if (!raw)
            continue;
        payload[field.field] = field.kind === 'identifier' ? raw.toLowerCase() : raw;
    }
    return payload;
}
/**
 * Which required fields this form has not collected yet — for disabling the submit button.
 *
 * ⚠️ Consent again: an unchecked required box is MISSING, not merely false.
 */
export function missingRegistrationFields(surface, values, consents = {}) {
    const missing = [];
    for (const field of surface.fields) {
        if (!field.required)
            continue;
        if (field.kind === 'consent') {
            if (!consents[field.field])
                missing.push(field.field);
            continue;
        }
        if (!text(values[field.field]))
            missing.push(field.field);
    }
    return missing;
}
/**
 * The autofill hint a password box on a REGISTRATION form must carry.
 *
 * ⚠️ Not `current-password`. That tells a password manager to fill the one it already holds
 * for this site, which on a sign-up form is the opposite of what is wanted, and is how a
 * person ends up registering a second account with the password of their first.
 */
export const NEW_PASSWORD_AUTOCOMPLETE = 'new-password';
/**
 * What to assume when the policy cannot be read.
 *
 * ⚠️ Deliberately only a length, and a modest one. Asserting a rule the deployment does not
 * have would refuse a password the server would accept — failing closed in the direction
 * that locks people out of their own recovery. The server refuses anyway; this is advice.
 */
export const FALLBACK_PASSWORD_POLICY = Object.freeze({
    minLength: 8,
    requireUppercase: false,
    requireLowercase: false,
    requireDigit: false,
    requireSpecial: false,
});
/** The specials `_validate_password` accepts, character for character. */
const PASSWORD_SPECIALS = new Set("!@#$%^&*()_+-=[]{}|;':\",./<>?");
/** Parse `GET /iam/sdk/auth/password-policy`'s `password_rules`, or `null`. */
export function readPasswordPolicy(payload) {
    if (!payload || typeof payload !== 'object')
        return null;
    const body = payload;
    // Accept either the whole `{password_rules, auth_policy}` envelope or just the rules, so
    // an application may proxy the route narrowed or whole without the client caring.
    const raw = (body.password_rules ?? body);
    const min = raw.min_length;
    if (typeof min !== 'number' || !Number.isFinite(min) || min < 1)
        return null;
    return {
        minLength: Math.floor(min),
        requireUppercase: raw.require_uppercase === true,
        requireLowercase: raw.require_lowercase === true,
        requireDigit: raw.require_digit === true,
        requireSpecial: raw.require_special === true,
    };
}
/** The rules, as sentences to show BEFORE somebody types — not as errors after. */
export function describePasswordPolicy(policy) {
    const rules = [`At least ${policy.minLength} characters`];
    if (policy.requireUppercase)
        rules.push('An uppercase letter');
    if (policy.requireLowercase)
        rules.push('A lowercase letter');
    if (policy.requireDigit)
        rules.push('A number');
    if (policy.requireSpecial)
        rules.push('A special character');
    return rules;
}
/**
 * Which rules this password does NOT meet. Empty means the client has no objection.
 *
 * ⚠️ "No objection" is not "accepted": the server validates independently and may know
 * rules this client does not. Never treat an empty result as permission.
 */
export function checkPassword(value, policy) {
    const unmet = [];
    const password = typeof value === 'string' ? value : '';
    if (password.length < policy.minLength)
        unmet.push(`At least ${policy.minLength} characters`);
    if (policy.requireUppercase && !/[A-Z]/.test(password))
        unmet.push('An uppercase letter');
    if (policy.requireLowercase && !/[a-z]/.test(password))
        unmet.push('A lowercase letter');
    if (policy.requireDigit && !/[0-9]/.test(password))
        unmet.push('A number');
    if (policy.requireSpecial && ![...password].some((c) => PASSWORD_SPECIALS.has(c))) {
        unmet.push('A special character');
    }
    return unmet;
}
/**
 * The codes a sign-in or recovery page has an actual branch for. ⚠️ This is deliberately
 * NOT the whole vocabulary — Identity Studio spells 41 codes and most of them a page can
 * only render as prose. Adding one here means "some page does something different because
 * of it", and a constant nothing branches on is noise that reads as coverage.
 */
export const REFUSAL = Object.freeze({
    /** ⭐ The one all four were matching `/verif/i` for: offer "resend verification". */
    EMAIL_UNVERIFIED: 'email_unverified',
    /** Unknown address, wrong password, or no access — deliberately one code. See below. */
    INVALID_CREDENTIALS: 'invalid_credentials',
    /** Identity-level lock. Resolved by Identity Studio, on the identity plane's terms. */
    ACCOUNT_LOCKED: 'account_locked',
    /** THIS application's administrator suspended them. A different support desk entirely. */
    APPLICATION_LOCKED: 'application_locked',
    /** The application does not offer password sign-in; do not ask for a password again. */
    PASSWORD_LOGIN_DISABLED: 'password_login_disabled',
    /** A second factor is needed. Arrives as a 200 `next_step`, not as a refusal body. */
    MFA_REQUIRED: 'mfa_required',
    /** The second factor supplied was wrong. The person retries; nothing else changes. */
    INVALID_MFA_CODE: 'invalid_mfa_code',
    /** Throttled. `retry_after_seconds` rides beside it — AG-1584 gap 5. */
    RATE_LIMITED: 'rate_limited',
    /** A mailed secret that we never issued. "Request a new one" is the only remedy. */
    VERIFICATION_TOKEN_INVALID: 'verification_token_invalid',
    RESET_TOKEN_INVALID: 'reset_token_invalid',
    MAGIC_LINK_INVALID: 'magic_link_invalid',
    /** A mailed secret that HAS expired. Different remedy: ask again and it will work. */
    VERIFICATION_TOKEN_EXPIRED: 'verification_token_expired',
    RESET_TOKEN_EXPIRED: 'reset_token_expired',
    MAGIC_LINK_EXPIRED: 'magic_link_expired',
    /** A magic link that was already spent — single use is the design, not a fault. */
    MAGIC_LINK_USED: 'magic_link_used',
    /** ⚠️ The caller's own integration is wrong. Do NOT render this at the person. */
    INVALID_REQUEST: 'invalid_request',
    /**
     * ⭐ THE IDENTITY PROVIDER COULD NOT BE REACHED — nothing was judged, so nothing the person
     * typed is known to be wrong. AG-1805, 2026-09-16.
     *
     * ⛔⛔ THE DEFECT THIS CLOSES, and the conformance gate had been reporting it for weeks
     * before anybody read it as a product bug rather than as noise. All four applications built
     * their outage refusal as `f"Identity Studio is unreachable: {exc}"`, flattened it to a
     * **400**, and sent it to a browser. Three things wrong at once:
     *
     *   * `httpx.ConnectTimeout()` and `ReadTimeout()` carry NO message, so the sentence a
     *     person actually saw was `Identity Studio is unreachable: ` — ending in a colon with
     *     nothing after it;
     *   * it names OUR internal service to somebody who has never heard of it, on a page where
     *     they are trying to sign in;
     *   * it carried **no code at all**, so a page could not tell an outage from a bad password
     *     and had nothing to offer but the same form again.
     *
     * ⚠️ 400 was the worst part. `IdentityStudioUnavailable`'s own docstring had said the right
     * answer all along — *"a rejected credential is the caller's problem (401); an IAM we cannot
     * reach is ours (503), and reporting our outage as their bad password sends them to reset it
     * in the middle of it."* The class knew; the routes flattened it anyway.
     *
     * ⇒ A page seeing this must say the service is briefly unavailable and offer **retry** —
     * never "check your details", and never a password reset.
     */
    IDENTITY_UNAVAILABLE: 'identity_unavailable',
    // ── Account lifecycle — AG-1803 ───────────────────────────────────────────────
    //
    // ⭐ Added for the sign-up journey, from `auth_refusals.py`'s own names. The codes are
    // spelled in exactly two places on purpose — there, and here — so a page never has to
    // infer one from a sentence.
    /**
     * ⭐ DISCLOSED DELIBERATELY, and it is not a hole in `invalid_credentials`' coarseness.
     * `sdk_signup` checks the registration GATE before it looks the address up, so a closed
     * application is not an oracle — and a sign-up form that cannot say "that address is
     * taken" is a form nobody can complete. ⇒ Offer "sign in instead".
     */
    EMAIL_ALREADY_REGISTERED: 'email_already_registered',
    /**
     * The password does not meet the platform policy. ⚠️ The PROSE carries which rule failed
     * and a page should render it; the code only says which field to attach it to.
     */
    PASSWORD_POLICY: 'password_policy',
    /** Onboarding asked to create an organization for somebody who already has one. */
    ALREADY_ONBOARDED: 'already_onboarded',
    /** This application does not admit this person at all — a different remedy entirely. */
    NO_APPLICATION_ACCESS: 'no_application_access',
});
/**
 * Refusals that mean "this application's backend sent something wrong", never "you typed
 * something wrong".
 *
 * ⭐ Worth its own set because the failure mode it prevents is invisible: without it a page
 * shows somebody "email and password are required" when the form DID send both and the
 * proxy dropped them, and that person retypes a correct password until they give up.
 */
const INTEGRATION_REFUSALS = new Set([REFUSAL.INVALID_REQUEST]);
/** Whether a refusal is the integration's fault rather than the person's. */
export function refusalIsOurs(code) {
    return code !== null && INTEGRATION_REFUSALS.has(code);
}
/**
 * Refusals a person can act on by asking for a fresh secret to be sent.
 *
 * ⚠️ INVALID and EXPIRED are both here, and that is the point of reading them together: the
 * remedy is the same button, and only the sentence beside it differs. A page that branches
 * on the pair separately writes the same handler twice.
 */
const RESENDABLE = new Set([
    REFUSAL.VERIFICATION_TOKEN_INVALID, REFUSAL.VERIFICATION_TOKEN_EXPIRED,
    REFUSAL.RESET_TOKEN_INVALID, REFUSAL.RESET_TOKEN_EXPIRED,
    REFUSAL.MAGIC_LINK_INVALID, REFUSAL.MAGIC_LINK_EXPIRED, REFUSAL.MAGIC_LINK_USED,
]);
/**
 * Refusals that are OUR outage rather than anything the person did — AG-1805.
 *
 * ⛔ The remedy is **try again**, and a page must offer nothing else: not "check your
 * details", and above all not a password reset. Sending somebody to reset a password that was
 * never judged wrong is how a one-minute deploy blip turns into a support ticket and a
 * changed credential.
 *
 * ⚠️ Deliberately its own predicate rather than folded into `refusalIsOurs`. That one means
 * "this application's integration is broken, do not render it at the person"; this one means
 * "the service is briefly down, tell them plainly and let them retry". Different sentences,
 * different controls.
 */
const TRANSIENT = new Set([REFUSAL.IDENTITY_UNAVAILABLE]);
export function refusalIsTransient(code) {
    return code !== null && TRANSIENT.has(code);
}
/** Whether the remedy for this refusal is "send me another one". */
export function refusalIsResendable(code) {
    return code !== null && RESENDABLE.has(code);
}
/**
 * ⛔ THE LEGACY GUESS, IN ONE PLACE INSTEAD OF FOUR.
 *
 * Identity Studio started answering `error` on 2026-09-14; the four applications adopt it
 * one repository at a time, and a page may talk to an older deployment at any point in
 * that window — the same "could not ask is not a verdict" rule the plan fetch follows.
 * So the prose heuristic survives, exactly once, HERE, where deleting it is a one-line
 * change in one file rather than an archaeology exercise across four.
 *
 * ⚠️ It stays as narrow as the four regexes it replaces. Widening it would be building a
 * second vocabulary out of English — precisely the thing the codes exist to end — and a
 * wrong guess is worse than no guess, because a page acts on it.
 */
function guessRefusalFromProse(message) {
    const lower = message.toLowerCase();
    if (!lower)
        return null;
    if (/\bverif/.test(lower) && !/token/.test(lower))
        return REFUSAL.EMAIL_UNVERIFIED;
    return null;
}
/**
 * Read a refusal from whatever an application's backend passed through.
 *
 * ⚠️ TOLERANT ABOUT SHAPE ON PURPOSE. The four applications' proxies do not agree on how
 * they relay Identity Studio's body — some forward it, some rewrap it under `detail`, some
 * hand the page an `Error` whose `message` is the sentence and nothing else. All of those
 * arrive here and all of them must produce an answer, because the alternative is each page
 * writing its own unwrapper and the drift starting again one layer down.
 *
 * ⛔ `code` is `null` rather than a guess when nothing identifies the refusal. A page shows
 * `message` and offers nothing extra, which is exactly what it did before this existed.
 */
export function readRefusal(payload) {
    const empty = { code: null, message: '', guessed: false, retryAfterSeconds: null };
    if (payload instanceof Error) {
        const message = text(payload.message);
        // ⭐ MEASURED, not guessed: all four applications throw an `ApiError` that keeps the
        // parsed body on `.payload` (`Analytics|data-studio|Converse|api-workflow src/lib/api.ts`
        // — the same class four times, which is its own AG-1583-shaped observation). `body` and
        // `response` follow only so a fifth consumer with a different client is not forced to
        // unwrap by hand before calling this.
        const carrier = payload;
        const carried = carrier.payload ?? carrier.body ?? carrier.response;
        if (carried && typeof carried === 'object') {
            const inner = readRefusal(carried);
            if (inner.code)
                return { ...inner, message: inner.message || message };
        }
        const guess = guessRefusalFromProse(message);
        return { code: guess, message, guessed: guess !== null, retryAfterSeconds: null };
    }
    if (typeof payload === 'string') {
        const message = text(payload);
        const guess = guessRefusalFromProse(message);
        return { code: guess, message, guessed: guess !== null, retryAfterSeconds: null };
    }
    if (!payload || typeof payload !== 'object')
        return empty;
    const body = payload;
    // `detail` may itself be the whole Identity Studio body when a proxy nested it.
    if (body.detail && typeof body.detail === 'object') {
        const inner = readRefusal(body.detail);
        if (inner.code || inner.message)
            return inner;
    }
    const message = text(body.detail) || text(body.message) || text(body.error_description);
    const declared = text(body.error) || text(body.code);
    const retry = typeof body.retry_after_seconds === 'number' ? body.retry_after_seconds : null;
    if (declared)
        return { code: declared, message, guessed: false, retryAfterSeconds: retry };
    const guess = guessRefusalFromProse(message);
    return { code: guess, message, guessed: guess !== null, retryAfterSeconds: retry };
}
/**
 * Decide whether a stored token may still be served — AG-1788.
 *
 * ⛔ FAIL SOFT, AND `keep` IS THE SOFT ANSWER. A probe that could not be made — Identity
 * Studio mid-deploy, a CSP blocking the origin (AG-1845 on Analytics), a network blip —
 * must not sign anybody out. "Could not ask" is not a verdict, the same rule AG-1542 settled
 * for session liveness and §4 for the flow plan. ⇒ Only a probe that ANSWERED, and named
 * somebody, can take a session away.
 *
 * ⚠️ `hostOwnedIdentity` short-circuits before anything else. An embedded Converse identity
 * belongs to whoever the HOST signed in, and the browser's suite cookie belongs to whoever
 * last used Identity Studio on that machine — comparing them would sign out a correct
 * embedded user because a different person used the same laptop. Checked BEFORE the probe is
 * even made, which is why this function takes the flag rather than the probe result alone.
 */
export function decideStaleSession(args) {
    if (args.hostOwnedIdentity) {
        return { action: 'keep', reason: 'host-owned identity — the suite cookie is not ours to read' };
    }
    if (!args.stored) {
        return { action: 'keep', reason: 'no local session to compare' };
    }
    if (!args.probe) {
        // ⛔ The soft answer. A probe we could not make says nothing about who is signed in.
        return { action: 'keep', reason: 'could not ask — not a verdict' };
    }
    const shared = args.probe.shared === true;
    const suite = args.probe.user ?? null;
    const local = args.stored;
    if (!hasAnyIdentity(suite)) {
        // ⛔⛔ AN ABSENCE IS NOT A VERDICT — and this being `signOut` signed everybody out of
        // Analytics on EVERY PAGE LOAD. Measured on dev 2026-09-17, in this order:
        //
        //     GET /v1/auth/config            200
        //     GET /v1/auth/me                200   ← the API served the local token
        //     GET /v1/auth/sso/start         200
        //     GET {identity}/sso/session     200   {"shared":false}
        //     → local session cleared, redirect to /login
        //
        // The suite cookie (`og_at`) had expired twelve days earlier while the Analytics access
        // token still had twenty-four hours left on it. Two tokens with two lifetimes: the
        // shorter one ending says NOTHING about the longer one, and the API had just proved it
        // by answering 200 to the very request this then threw away.
        //
        // ⇒ A BARE `shared: false` is the answer a signed-out browser, a revoked shared sign-in, an
        // expired suite cookie, a browser blocking third-party cookies, and an application
        // outside any suite ALL give. It cannot distinguish them, so it is evidence of nothing
        // about the local token. `readSuiteApplications` already says exactly this in its own
        // docblock; this branch was the one place that read it as an accusation.
        //
        // ⇒ WHO REVOKES: the API, by rejecting the token — and the 401 handler signs out on
        // that. This function may only ever settle "is the stored token a DIFFERENT PERSON from
        // the one the suite names", which needs the suite to actually name somebody. Nobody
        // named, nothing to compare, and "I cannot compare these" is already `keep` twice below.
        //
        // ⚠️ PROMOTED, NOT WRITTEN HERE (AG-2237). This fix was measured and applied in
        // Analytics' VENDORED copy on 2026-09-17 and never reached the canonical package, so
        // `sync-auth-flow.mjs --check` reported "edited in both places" and correctly refused to
        // name a winner — while Automation Studio, Data Studio and Converse went on shipping the
        // defect. Fixing an instance had left the class open in three applications.
        return { action: 'keep', reason: 'the suite named nobody — that is not evidence about this token' };
    }
    if (!shared) {
        // ⛔⛔ AG-2706 — A REFUSAL THAT NAMES SOMEBODY IS EVIDENCE, AND THIS USED TO DISCARD IT.
        //
        // Measured on dev 2026-10-05, in PPT Builder and File Manager: the browser held one
        // person's stored session, and its suite cookie belonged to a second person who had no
        // grant on the application. The probe answered
        //
        //     {"shared": false, "reason": "no_application_access", "user": {"email": <the second>}}
        //
        // and the branch above read every `shared: false` as "nobody named". So the application
        // served the FIRST person's decks and files to whoever holds the second person's suite
        // session — the AG-1788 defect, back through the one door the comparison never opened.
        //
        // ⇒ Identity Studio puts a `user` under `shared: false` only when the cookie resolves a
        // LIVE suite session and the refusal names an account (`auth_refusals.names_an_account`:
        // `no_application_access`, `mfa_required`). That is not an absence; it says exactly who
        // signed in to the suite in this browser most recently. A different person from the
        // stored token means the stored token is not theirs to be served.
        //
        // ⚠️ Still fail-soft everywhere else. The SAME person refused here keeps: whether they may
        // still use this application is the API's question, answered by its own 401/403. Nothing
        // comparable keeps, by the rule below. An unreadable local token keeps too — unlike the
        // `shared` case, the suite is not offering a session to re-enter, so dropping it would
        // only sign somebody out on a guess.
        const refusedPair = hasAnyIdentity(local) ? comparable(local, suite) : null;
        return refusedPair !== null && refusedPair[0] !== refusedPair[1]
            ? { action: 'replace', reason: 'the suite refused a different person from the stored token — the stored token is not theirs' }
            : { action: 'keep', reason: 'the suite refused this same person, or nobody comparable — access is the API\'s to decide' };
    }
    if (!hasAnyIdentity(local)) {
        // We hold a token whose identity we cannot read. It cannot be shown to match, and the
        // suite has named somebody, so re-entering the silent leg is both safe and correct.
        return { action: 'replace', reason: 'the stored token names nobody we can compare' };
    }
    const pair = comparable(local, suite);
    if (pair === null) {
        // ⛔⛔ NOTHING IN COMMON TO COMPARE — and this is `keep`, not `replace`.
        //
        // This case is not hypothetical: it was a LIVE OUTAGE on all four applications. The
        // suite probe answers `{name, email, avatar_url}` and carries **no subject**, while
        // every application passed `sub: <its own user id>`. The first version of this function
        // chose a field per side independently, so it compared `sub:4279e52d…` from the
        // application against `email:someone@…` from the suite — values that can never be equal.
        // Verdict `replace`, session cleared, redirect to `/login`, on every load: signing in
        // became impossible everywhere at once.
        //
        // ⇒ Two identifiers from different namespaces are not evidence of a different person.
        // "I cannot compare these" is the same class of answer as "I could not ask", and it gets
        // the same fail-soft treatment.
        return { action: 'keep', reason: 'no identifier the two sides share — cannot compare' };
    }
    return pair[0] === pair[1]
        ? { action: 'keep', reason: 'the stored token is the person the suite says is signed in' }
        : { action: 'replace', reason: 'the stored token is a different person from the suite session' };
}
/** Whether a principal carries anything at all we could compare on. */
function hasAnyIdentity(who) {
    return Boolean(who && (text(who.sub) || text(who.email)));
}
/**
 * The two sides reduced to ONE shared namespace, or `null` when they share none.
 *
 * ⛔ THE RULE THAT WAS MISSING: a field is only usable if **both** sides carry it. Choosing
 * the strongest identifier available on each side independently is what produced the outage
 * described above — it compares a subject to an address and calls the difference a different
 * person.
 *
 * ⚠️ `sub` first when both have one: an address can be changed, and is compared
 * case-insensitively, which is a weaker equality than it looks. But a subject on one side and
 * an address on the other is not a weaker comparison — it is not a comparison at all.
 */
function comparable(local, suite) {
    const localSub = text(local?.sub);
    const suiteSub = text(suite?.sub);
    if (localSub && suiteSub)
        return [localSub, suiteSub];
    const localEmail = text(local?.email).toLowerCase();
    const suiteEmail = text(suite?.email).toLowerCase();
    if (localEmail && suiteEmail)
        return [localEmail, suiteEmail];
    return null;
}
/**
 * Build the plant request, or `null` when there is nothing to do.
 *
 * ⛔ PURE, and `null` is a completely ordinary answer. A sign-in returns no plant token when
 * the application is in no sharing suite, or when Identity Studio predates the endpoint. A
 * caller must treat that as "no suite cookie this time" and carry on — **never** as a failed
 * sign-in. The person is signed in to this application either way.
 *
 * ⚠️ `loginUrl` is the hosted sign-in URL the application already has in its own
 * `/v1/auth/config` (`urls.login` / `hosted_login_url`), and the plant endpoint is derived from
 * it by swapping the last segment. That is deliberate: it reuses the one URL the platform has
 * already validated against this application's registered domains, so no new setting exists to
 * be configured wrongly — the same reasoning `sso.py` uses for `issuer + "/login"`.
 *
 * ⛔ And it must be the HOSTED host, never the API host. `ids_suite_session` is set with no
 * `Domain`, so it is host-only: a plant on `api.dev.identity.vaarta.ai` would set a cookie
 * nothing ever reads.
 */
export function suitePlantRequest(args) {
    const token = text(args.token);
    const loginUrl = text(args.loginUrl);
    const redirect = text(args.returnTo);
    if (!token || !loginUrl || !redirect)
        return null;
    let action;
    try {
        const url = new URL(loginUrl);
        // `/app/{key}/login` → `/app/{key}/sso/plant`. Split rather than string-replace so a key
        // that happens to contain "login" cannot be mangled.
        const segments = url.pathname.split('/').filter(Boolean);
        if (!segments.length)
            return null;
        segments[segments.length - 1] = 'sso';
        url.pathname = '/' + segments.join('/') + '/plant';
        url.search = '';
        url.hash = '';
        action = url.toString();
    }
    catch {
        // An unparseable login URL is a misconfiguration, and a sign-in must not fail on it.
        return null;
    }
    return { action, fields: { token, redirect_uri: redirect } };
}
/**
 * Submit a plant request by navigating the browser — the one impure function here besides
 * `fetchFlowPlan`.
 *
 * ⛔ A FORM POST, NOT A REDIRECT, and that is the whole reason this does DOM work instead of
 * setting `location.href`. The token is a single-use 60-second credential; a `GET` would put it
 * in the URL, which reaches browser history, the `Referer` header and every proxy in between.
 * That is the hazard AG-1942 was filed for on a different route, and it is cheaper not to
 * create it than to scrub it afterwards.
 *
 * ⚠️ Returns `false` when there is no document to submit into (server rendering, tests), so a
 * caller can branch without a `typeof window` check of its own.
 */
export function submitSuitePlant(request) {
    if (!request)
        return false;
    if (typeof document === 'undefined' || !document.body)
        return false;
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = request.action;
    // ⚠️ Not `display:none` — a hidden form still submits, and some browsers decline to submit a
    // form that was never in the document. Appended, submitted, and the navigation replaces us.
    form.style.display = 'none';
    for (const [name, value] of Object.entries(request.fields)) {
        const input = document.createElement('input');
        input.type = 'hidden';
        input.name = name;
        input.value = value;
        form.appendChild(input);
    }
    document.body.appendChild(form);
    // ⛔ AG-3108 — this tab is about to hold a suite cookie, so a stored "nobody is signed in" is now false. Forgotten
    // only here, after the document check: a plant that cannot be submitted plants nothing.
    forgetEverySuiteTabMemory();
    form.submit();
    return true;
}
/* ──────────────────────────────────────────────────────────────────────────────────────
 * AG-1542 / AG-1989 — ending the SHARED sign-in, not just this application's
 * ────────────────────────────────────────────────────────────────────────────────────── */
/**
 * End the suite session and every sibling application's session under it.
 *
 * ⛔⛔ WHY THIS EXISTS, and it is the sign-out twin of `suitePlantRequest`'s defect.
 * `sdk_logout` already revokes the suite session and its children by default — but it clears
 * the suite COOKIE only `if response is not None`, which is exactly the case an application's
 * server-side SDK call is not. Same line, same shape, opposite direction: the plant bug was a
 * cookie never set, this is a cookie never cleared.
 *
 * ⇒ Signing out of an application left the browser holding a suite credential. What the
 * person saw depended on whether their application had sent a `refresh_token`: a revoked
 * session and a dead cookie, or a LIVE suite session that signed them straight back in on the
 * next page load. Neither is "I signed out".
 *
 * ⭐ Called from the BROWSER, not from the application's backend, and that is the whole point:
 * only the browser holds `ids_suite_session`, and Identity Studio can only clear a cookie on a
 * response the browser is actually reading. `credentials: 'include'` is what carries it. This
 * became possible when AG-1543 derived the CORS allowlist from each application's registered
 * domains — before that the browser discarded the answer even though the server gave it.
 *
 * ⛔ FIRE AND FORGET, and it NEVER throws. A sign-out that fails because Identity Studio is
 * unreachable must still sign the person out locally — the local session goes either way, so
 * "sign out" never becomes a button that does nothing. Identity Studio 502s its siblings for
 * about a minute on every deploy, and that minute must not be one where nobody can leave.
 *
 * ⚠️ `url` comes from the application's own `/v1/auth/config` (`urls.suite_logout`), never
 * assembled here. It must be on the HOSTED host: `ids_suite_session` carries no `Domain`, so a
 * call to the API host presents no cookie and clears nothing — the same trap that cost Data
 * Studio a week in AG-1533.
 *
 * Returns `true` only when Identity Studio confirmed it. `false` means "could not ask", which
 * a caller must treat as ordinary rather than as a failed sign-out.
 */
export async function suiteLogout(url) {
    const endpoint = text(url);
    if (!endpoint)
        return false;
    try {
        const response = await fetch(endpoint, {
            method: 'POST',
            credentials: 'include',
            // ⚠️ No body and no JSON content type on purpose. The cookie IS the request: the route
            // takes no parameters, and a `Content-Type: application/json` would make this a
            // preflighted request for no gain.
            headers: { Accept: 'application/json' },
        });
        return response.ok;
    }
    catch {
        return false;
    }
}
/**
 * Derive the suite-logout URL from the hosted login URL an application already has.
 *
 * ⚠️ A FALLBACK, not the primary path. `urls.suite_logout` from the application's own
 * `/v1/auth/config` is the value to use; this exists so an application talking to a backend
 * that predates that field can still end the shared session, rather than silently doing half
 * a sign-out. Same derivation `suitePlantRequest` uses, for the same reason: it reuses the one
 * URL the platform has already validated against this application's registered domains.
 *
 * `null` when there is nothing to derive from.
 */
export function suiteLogoutUrlFrom(loginUrl) {
    const value = text(loginUrl);
    if (!value)
        return null;
    try {
        const url = new URL(value);
        const segments = url.pathname.split('/').filter(Boolean);
        if (!segments.length)
            return null;
        // `/app/{key}/login` → `/app/{key}/sso/logout`. Split rather than string-replace so an
        // app key that happens to contain "login" cannot be mangled.
        segments[segments.length - 1] = 'sso';
        url.pathname = '/' + segments.join('/') + '/logout';
        url.search = '';
        url.hash = '';
        return url.toString();
    }
    catch {
        return null;
    }
}
/* ══════════════════════════════════════════════════════════════════════════════
   SUITE SSO — THE TENANCY CHOICE, RENDERED BY THE APPLICATION. AG-2207.
   ══════════════════════════════════════════════════════════════════════════════

   ⭐ WHAT THIS CLOSES, AND IT IS NOT A CONVENIENCE. `/sso/continue` answers somebody with
   more than one candidate tenancy by returning the browser to the application's own
   `redirect_uri` with `sso=choose_tenant` and a short-lived `choice_token` — AG-2207 moved
   the picker out of Identity Studio, which was the last hosted page any suite user could
   reach. But `choose_tenant` is an `sso` value no application recognises, and all four
   `decideCallbackArrival` copies end with `if (refusal) return { action: 'refused' }`.

   ⛔ So the measured behaviour today is not "the SSO convenience is lost". It is a DEAD END
   that names the wrong culprit: "Identity Studio did not complete the sign-in
   (choose_tenant)." in Analytics and Automation Studio, "Identity Studio refused the
   sign-in." in Converse and Data Studio. Nobody in two organisations can sign in through
   the silent leg at all, and the message sends them to ask Identity Studio about a refusal
   that never happened.

   ⚠️ Here rather than in four `suite-sso.ts` files for the reason AG-1583 already paid for
   once: `decideCallbackArrival` was four copies of one decision that drifted into two
   answers and shipped an Identity Studio page to the users of two products. This is the
   same decision growing a fifth branch.

   THE FLOW, once, so no page has to reconstruct it:

     1. `readTenancyChoice(location.search)`             → `{ choiceToken }`
     2. the application's own `/v1/auth/sso/start`        → `authorize_url`
     3. `fetchTenancies(authorizeUrl, choiceToken)`       → the candidate organisations
     4. the application renders ITS OWN picker
     5. `tenancyChoiceUrlFrom(authorizeUrl, chosen)`      → navigate; `/sso/continue`
                                                            re-validates and mints the code

   ⛔ STEP 2 IS NOT OPTIONAL AND IT IS NOT A ROUND-TRIP WORTH SAVING. The tenancies URL is
   an Identity Studio URL, and the standing rule is that every browser-facing one comes from
   the backend: `ids_suite_session` carries no `Domain`, so a URL built on the API host
   answers `no_shared_session` forever, and the two hosts are inverted
   (`dev.identity.vaarta.ai` vs `api.dev.identity.vaarta.ai`) in a way no frontend should be
   guessing at. `authorize_url` is a value the backend already composed from its own
   configuration, so deriving from it cannot invent a host. `/sso/start` builds it by
   formatting local settings — it makes no call to Identity Studio — so the cost is one
   request to the application's own API.
*/
/** The `sso` value that means "you are signed in, but say which organisation as". */
export const SSO_CHOOSE_TENANT = 'choose_tenant';
/**
 * Read a `?sso=choose_tenant&choice_token=…` arrival, or `null`.
 *
 * ⛔ BOTH or neither, the `readMagicLinkCallback` rule for the same reason: `choose_tenant`
 * with no token is not a picker to render with an empty list — it is a redirect that was
 * truncated or hand-edited, and there is nothing to ask `/sso/tenancies` with. The honest
 * answer is the one the page already has for a refusal it cannot act on.
 *
 * ⚠️ A page that acts on this should drop the token from its own URL —
 * `history.replaceState` — before rendering. The token is not a credential for anything
 * else (it mints nothing, names no tenancy, and `/sso/tenancies` is the only route that
 * accepts it), but it is live for ten minutes and a URL is the one place a secret gets
 * copied into a bug report, a referrer header and the back button.
 *
 * Takes anything `URLSearchParams`-shaped, or a query/URL string, so `useSearchParams()`,
 * a React Router `URLSearchParams` and `window.location.search` all satisfy it.
 */
export function readTenancyChoice(source) {
    const params = asParams(source);
    if (!params)
        return null;
    if (text(params.get('sso')) !== SSO_CHOOSE_TENANT)
        return null;
    const choiceToken = text(params.get('choice_token'));
    if (!choiceToken)
        return null;
    return { choiceToken };
}
/**
 * `/app/{key}/sso/tenancies` on the same host and app key the backend's `authorize_url`
 * names. `null` when there is nothing to derive from.
 *
 * Split rather than string-replaced, the `suiteLogoutUrlFrom` rule: an app key that happens
 * to contain "continue" must not be mangled. `redirect_uri` and `state` are dropped — this
 * route takes neither, and forwarding a redirect URI to a data endpoint would put it
 * somewhere it has no business being.
 */
export function tenancyListUrlFrom(authorizeUrl, choiceToken) {
    const url = ssoSibling(authorizeUrl, 'tenancies');
    if (!url)
        return null;
    url.search = '';
    const token = text(choiceToken);
    if (token)
        url.searchParams.set('token', token);
    return url.toString();
}
/**
 * The same `/sso/continue` the silent leg used, now carrying the answer.
 *
 * ⭐ `redirect_uri` and `state` are KEPT. This is the second half of one sign-in, not a new
 * one: the state is the application's own and its callback validates it on redeem, and the
 * redirect URI is the value Identity Studio checked against this application's registered
 * domains. Rebuilding either here would be inventing what the backend already decided.
 *
 * `null` when there is no authorize URL or no choice to declare — a `/sso/continue` with an
 * empty `application_tenant_id` is the silent leg again, which would ask the same question
 * and loop.
 */
export function tenancyChoiceUrlFrom(authorizeUrl, applicationTenantId) {
    const chosen = text(applicationTenantId);
    if (!chosen)
        return null;
    const value = text(authorizeUrl);
    if (!value)
        return null;
    try {
        const url = new URL(value);
        url.searchParams.set('application_tenant_id', chosen);
        return url.toString();
    }
    catch {
        return null;
    }
}
/** Read `/sso/tenancies`' answer. `null` when it is not a tenancy list at all. */
export function readTenancies(payload) {
    const raw = payload?.tenancies;
    if (!Array.isArray(raw))
        return null;
    const out = [];
    for (const entry of raw) {
        const applicationTenantId = text(entry?.application_tenant_id);
        // ⛔ The id is the only required field. A candidate with no readable id cannot be chosen,
        // so offering it would render a button that returns `tenant_not_available`. A missing
        // NAME is different: it is a label, and falling back to the organisation id shows
        // something selectable rather than an empty row.
        if (!applicationTenantId)
            continue;
        const organizationId = text(entry?.organization_id);
        out.push({
            applicationTenantId,
            organizationId,
            organizationName: text(entry?.organization_name) || organizationId || applicationTenantId,
            role: text(entry?.role),
        });
    }
    return out;
}
/**
 * Ask Identity Studio which organisations this sign-in may proceed as.
 *
 * ⛔⛔ THIS IS THE ONE FETCH IN THIS PACKAGE THAT DOES NOT FAIL SOFT TO `null`, and the
 * difference is the point. `fetchFlowPlan` returning `null` costs a configured sign-in
 * plan and falls back to email + password — a form that still works. There is no
 * equivalent fallback for *which customer's data you are about to be signed in to*.
 * Choosing one, or defaulting to the first, would sign somebody into the wrong
 * organisation, and a plausible wrong answer is worse than an admitted failure. So the
 * three ways this can fail are distinguished and handed back, and the caller's fallback is
 * its own sign-in form — never a tenancy.
 *
 * ⛔ `credentials: 'omit'`. The token is the authorisation and the suite cookie is host-only
 * on the sign-in origin, so it would not be sent from an application's page anyway.
 * Measured 2026-09-18 in real Chromium from all four suite origins: each application's own
 * origin reads its own `/sso/tenancies` (400 for a bogus token, body readable), and a
 * FOREIGN app key is refused `403 Request from unauthorized domain` — the per-application
 * boundary holds in the browser, not only in the response headers. ⚠️ `curl` with a
 * hand-set `Origin` does not establish either half; both readings were taken in a browser.
 */
export async function fetchTenancies(authorizeUrl, choiceToken, options = {}) {
    const token = text(choiceToken);
    // No token is not a network failure and must not read as one: there is nothing to ask
    // with, and "start signing in again" is the same answer an expired token gets.
    if (!token)
        return { ok: false, reason: 'expired' };
    const url = tenancyListUrlFrom(authorizeUrl, token);
    if (!url)
        return { ok: false, reason: 'unavailable' };
    const doFetch = options.fetchImpl ?? (typeof fetch === 'function' ? fetch : null);
    if (!doFetch)
        return { ok: false, reason: 'unavailable' };
    let response;
    try {
        response = await doFetch(url, { credentials: 'omit' });
    }
    catch {
        return { ok: false, reason: 'unavailable' };
    }
    if (response.status === 400 || response.status === 403)
        return { ok: false, reason: 'expired' };
    if (response.status === 401)
        return { ok: false, reason: 'signed_out' };
    if (!response.ok)
        return { ok: false, reason: 'unavailable' };
    let payload;
    try {
        payload = await response.json();
    }
    catch {
        return { ok: false, reason: 'unavailable' };
    }
    const tenancies = readTenancies(payload);
    // ⚠️ An unreadable body is `unavailable`, not an empty list. An empty list is a real
    // answer with its own meaning (see `decideTenancyChoice`) and conflating the two would
    // make a malformed response look like "you belong to nothing".
    if (!tenancies)
        return { ok: false, reason: 'unavailable' };
    return { ok: true, tenancies };
}
/**
 * What to do with the list that came back.
 *
 * ⚠️ `/sso/continue` only sends `choose_tenant` when it counted MORE THAN ONE candidate, so
 * one or zero here means the list changed between the two requests — a membership added or
 * removed in the seconds in between, or the ten minutes the token is live. Both are handled
 * rather than assumed away:
 *
 * * one — declare it. Asking somebody to choose between a single option is a dead control,
 *   and `/sso/continue` re-validates the declaration against the live candidates anyway
 *   (AG-1813), so nothing is trusted that was not going to be re-checked.
 * * zero — stop. Re-entering with nothing declared asks the same question and loops; the
 *   application's own form is the answer, and if the person genuinely has no tenancy the
 *   silent leg's own refusal vocabulary already says so.
 */
export function decideTenancyChoice(tenancies) {
    const list = (tenancies ?? []).filter((t) => text(t?.applicationTenantId));
    if (list.length === 0)
        return { action: 'none' };
    if (list.length === 1)
        return { action: 'auto', tenancy: list[0] };
    return { action: 'ask', tenancies: list };
}
/* ──────────────────────────────────────────────────────────────────────────────────────
 * AG-2454 — the suite sign-in decisions every application makes, once
 * ────────────────────────────────────────────────────────────────────────────────────── */
/**
 * ⛔⛔ WHY THESE LIVE HERE. Until AG-2454 they were six `suite-sso.ts` files, one per
 * application, copied from Converse's with a header that said "four small implementations
 * first, the shared client last, once the behaviour agrees". AG-1583 is what that cost:
 * four copies of `decideCallbackArrival` drifted into two answers and shipped an Identity
 * Studio page to the users of two products, invisibly, for a week. AG-2207 and AG-1989 then
 * each had to be fixed six times. Measured when they moved (2026-10-05), the six still
 * disagreed in three places: two applications kept a dead `hosted` branch (a "deliberate
 * press" nothing could set any more, which would have sent a person to an Identity Studio
 * page), three refused `/login` as a return path and two did not, and the parameter that
 * says "identity is on" had three names.
 *
 * ⇒ The DECISIONS are here and pure. What stays in each application is genuinely its own:
 * the sessionStorage key names (they deliberately differ — a shared key would let one
 * application's give-up verdict silence another's probe in the same tab), the default
 * return path, and any branch only that application's callback can receive.
 */
/** How many times one tab may bounce through the silent leg before it stops and shows the form. */
export const MAX_AUTO_ATTEMPTS = 3;
/** The code Identity Studio returns for "we know who you are, and you may not come in". */
export const NO_APPLICATION_ACCESS = 'no_application_access';
/**
 * What the sign-in surface should do on mount.
 *
 * The budget check is what keeps a misconfiguration from becoming a redirect loop: once a tab
 * has bounced `MAX_AUTO_ATTEMPTS` times it stops asking and shows the form. A sign-in page
 * that fails must fail into something a person can still use.
 */
export function decideLoginEntry(args) {
    if (!args.identityEnabled)
        return { action: 'form' };
    if (args.memory.asked)
        return { action: 'form' };
    if (args.memory.attempts >= MAX_AUTO_ATTEMPTS)
        return { action: 'form' };
    return { action: 'probe' };
}
/**
 * What `/auth/callback` should do with the URL it was reached by.
 *
 * ⛔ THERE IS NO `hosted` ANSWER, and AG-1583 is why. Two of the six copies still had one — a
 * `no_shared_session` after a "deliberate press" of a Continue-with-Vaarta button sent the
 * person to Identity Studio's hosted page. The button is gone (no Identity Studio page is ever
 * shown to a suite user, AG-1989) and nothing set the flag any more, so the branch was dead;
 * it is not carried forward to be revived by the next button somebody adds.
 *
 * ⭐ The bare arrival is not hypothetical, and Data Studio is where it was measured: Identity
 * Studio's hosted "Continue as <name>" can return with **no query parameters at all**, and a
 * callback that reads that as "no session" bounces to sign-in, which bounces back here,
 * forever. That cost a day. So a bare arrival re-enters the silent leg — the thing that
 * actually resolves it — and does so on a budget. It re-enters `authorize_url`, never
 * `login_url` (AG-1989): one copy navigated to an Identity Studio page there.
 *
 * ⚠️ `rememberedState` is Automation Studio's: its callback can arrive without `state` and it
 * keeps the value it sent. Every other application omits it, which changes nothing for them.
 */
export function decideCallbackArrival(args) {
    const params = new URLSearchParams(args.search);
    const code = params.get('code');
    const state = params.get('state') || text(args.rememberedState);
    if (code && state)
        return { action: 'redeem', code, state };
    const refusal = (params.get('sso') || '').trim();
    if (refusal === 'no_shared_session')
        return { action: 'form' };
    // ⛔⛔ BEFORE THE GENERIC REFUSAL, AND THAT ORDERING IS THE WHOLE POINT (AG-2207).
    //
    // `choose_tenant` is not a refusal. Falling through to `refused` made it a dead end in Data
    // Studio, and nobody in two organisations could use the silent leg at all.
    //
    // ⚠️ A `choose_tenant` with no token is a truncated or hand-edited redirect — there is
    // nothing to ask `/sso/tenancies` with. It is STILL not a refusal, so it gets the
    // application's own form rather than a message blaming Identity Studio.
    if (refusal === SSO_CHOOSE_TENANT) {
        const choice = readTenancyChoice(params);
        return choice ? { action: 'chooseTenant', choiceToken: choice.choiceToken } : { action: 'form' };
    }
    if (refusal)
        return { action: 'refused', reason: refusal };
    return args.memory.attempts + 1 > MAX_AUTO_ATTEMPTS ? { action: 'stop' } : { action: 'retry' };
}
/**
 * Whether a refusal means "we know who you are, and you may not come in".
 *
 * ⛔ Only the one code Identity Studio actually returns, deliberately, and not "anything
 * unrecognised": an unknown future reason gets the generic message rather than a confident
 * page asserting something about entitlement that nobody said.
 */
export function isAccessRefusal(reason) {
    return reason === NO_APPLICATION_ACCESS;
}
export function readRefusedIdentity(payload) {
    if (!payload || typeof payload !== 'object')
        return {};
    const body = payload;
    // Only ever from the refusal this page is actually rendering. A body that says something
    // else — `shared: true`, say, because access was granted between the redirect and this
    // call — must not be used to decorate a page claiming the opposite.
    if (body.reason !== NO_APPLICATION_ACCESS)
        return {};
    const value = (raw) => {
        const trimmed = typeof raw === 'string' ? raw.trim() : '';
        return trimmed.length > 0 ? trimmed : undefined;
    };
    return {
        name: value(body.user?.name),
        email: value(body.user?.email),
        application: value(body.application),
    };
}
/**
 * Sanitize a remembered destination before navigating to it.
 *
 * It travels through `sessionStorage` and comes back as a string the page is about to navigate
 * to. Only an in-app absolute path is acceptable: `//evil.example` is protocol-relative and
 * leaves the site, and anything with a scheme leaves it more obviously. Never back into the
 * flow either — `/auth/*`, or `/login`, which three of the six copies refused and two did not
 * (the two with no `/login` route, where refusing it costs nothing). A destination that fails
 * the check is not an error; it means "go to the default".
 */
export function safeReturnPath(value) {
    const raw = (value || '').trim();
    if (!raw.startsWith('/'))
        return null;
    if (raw.startsWith('//'))
        return null;
    if (raw.startsWith('/auth/') || raw === '/login')
        return null;
    return raw;
}
/**
 * ⛔ AG-3108 — every tab memory this bundle created, so the moment the browser is about to hold a suite cookie can
 * forget them all.
 *
 * A tab memory's `asked` is written when the silent leg comes back "nobody is signed in". That is true when it is
 * written and stops being true the moment this tab signs in through the application's own form, because that sign-in
 * plants the suite cookie (`submitSuitePlant`). Nothing cleared it there — only the suite CALLBACK forgot — so a tab
 * that had once been signed out kept showing the password form with a live suite sign-in, for as long as it stayed
 * open (measured on dev in Converse, 2026-10-13, `ids_sso_asked=1` while signed in). The six applications each own
 * a memory with their own key names, and all six plant through this package, so the forgetting lives here: one
 * place, and an application added later cannot leave it out.
 */
const suiteTabMemories = new Set();
/** Forget every tab memory in this bundle — what a plant does, exported for a host that ends a sign-in elsewhere. */
export function forgetEverySuiteTabMemory() {
    for (const forget of suiteTabMemories)
        forget();
}
/**
 * One application's tab memory for the silent leg.
 *
 * ⛔ EVERY ACCESS IS WRAPPED. `sessionStorage` throws outright in a partitioned or
 * storage-blocked context, and a sign-in page must not be the thing that breaks. A tab that
 * cannot remember asks again — the budget is then enforced by the server's rate limiter
 * rather than here, which is a worse experience but not an unsafe one.
 *
 * ⚠️ `legacyAttemptKeys` are flags earlier releases wrote ('1' = "already bounced once"),
 * still honoured so a tab open across a deploy does not get a fresh budget; `retiredKeys` are
 * removed by `forget` and read by nothing. Both exist so moving the decisions here changed no
 * application's behaviour — not as an invitation to add more.
 */
export function createSuiteTabMemory(options) {
    const { keys } = options;
    const legacy = options.legacyAttemptKeys ?? [];
    const retired = options.retiredKeys ?? [];
    const store = () => {
        try {
            return options.storage ? options.storage() ?? null : globalThis.sessionStorage ?? null;
        }
        catch {
            return null;
        }
    };
    const get = (key) => {
        try {
            return store()?.getItem(key) ?? null;
        }
        catch {
            return null;
        }
    };
    const put = (key, value) => {
        try {
            store()?.setItem(key, value);
        }
        catch {
            /* see the docblock: a tab that cannot remember asks again */
        }
    };
    const drop = (key) => {
        try {
            store()?.removeItem(key);
        }
        catch {
            /* nothing to forget */
        }
    };
    const read = () => {
        const raw = Number(get(keys.attempts) || '0');
        const counted = Number.isFinite(raw) && raw > 0 ? raw : 0;
        const fromLegacy = legacy.some((key) => get(key) === '1') ? 1 : 0;
        return { attempts: Math.max(counted, fromLegacy), asked: get(keys.asked) === '1' };
    };
    const forget = () => {
        drop(keys.attempts);
        drop(keys.asked);
        for (const key of [...legacy, ...retired])
            drop(key);
    };
    suiteTabMemories.add(forget);
    return {
        keys,
        read,
        countAttempt() {
            const next = read().attempts + 1;
            put(keys.attempts, String(next));
            return next;
        },
        stopAsking() {
            put(keys.asked, '1');
        },
        forget,
        rememberReturnPath(path) {
            const safe = safeReturnPath(path);
            if (safe)
                put(keys.returnPath, safe);
        },
        takeReturnPath(fallback = '/') {
            const stored = safeReturnPath(get(keys.returnPath));
            drop(keys.returnPath);
            return stored || fallback;
        },
    };
}
/* ── shared plumbing for the two URL derivations ──────────────────────────── */
/** `{ids}/app/{key}/sso/continue?…` → a `URL` for `{ids}/app/{key}/sso/{leaf}`. */
function ssoSibling(authorizeUrl, leaf) {
    const value = text(authorizeUrl);
    if (!value)
        return null;
    try {
        const url = new URL(value);
        const segments = url.pathname.split('/').filter(Boolean);
        if (segments.length < 2)
            return null;
        // ⛔ Only ever the LAST segment, and only when it is the one we expect. A URL that is
        // not `/sso/continue` is not an authorize URL, and rewriting its tail would produce a
        // confident wrong address on a backend that changed shape.
        if (segments[segments.length - 1] !== 'continue')
            return null;
        segments[segments.length - 1] = leaf;
        url.pathname = '/' + segments.join('/');
        url.hash = '';
        return url;
    }
    catch {
        return null;
    }
}
/** `URLSearchParams`, a query string or a whole href — the `readMagicLinkCallback` reader. */
function asParams(source) {
    if (!source)
        return null;
    if (typeof source === 'string') {
        try {
            return source.includes('://')
                ? new URL(source).searchParams
                : new URLSearchParams(source.startsWith('?') ? source.slice(1) : source);
        }
        catch {
            return null;
        }
    }
    return typeof source.get === 'function' ? source : null;
}
